import React, { Component, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';

import classes from './ListingPageToolbar.scss';

@connect(
  (state) => ({
    isMobile: state.app.isMobile
  })
)
@withTranslation(['main'], { i18n, wait: !!window })
export default class ListingPageToolbar extends Component {
  static propTypes = {
    orderKeys: PropTypes.array,
    totalText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    selectedSort: PropTypes.string,
    onChangeSort: PropTypes.func,
    onChangeView: PropTypes.func,
    exportToExcel: PropTypes.func,
    view: PropTypes.oneOf(['list', 'table']),
    t: PropTypes.func,
    exports: PropTypes.object,
    suggestOffer: PropTypes.node,
    isMobile: PropTypes.bool
  };

  static defaultProps = {
    totalText: false,
    orderKeys: [],
  }

  handleChangeOrder(newSort) {
    const { selectedSort, orderKeys } = this.props;
    const previousSortDir = selectedSort && selectedSort[0] === '-' ? 'desc' : 'asc';
    const previousSort = previousSortDir === 'desc' ? selectedSort.substr(1) : selectedSort;
    const newOrderKey = orderKeys.filter(orderKey => orderKey.name === newSort)[0];
    const defaultAsc = newOrderKey ? newOrderKey.defaultAsc : true;
    const newSortDir = newSort === previousSort ? (
      previousSortDir === 'asc' ? 'desc' : 'asc'
    ) : (
      defaultAsc ? 'asc' : 'desc'
    )
    ;

    this.props.onChangeSort(newSortDir === 'asc' ? newSort : '-' + newSort);
  }

  renderSort() {
    const { t, orderKeys, selectedSort } = this.props;

    if (!orderKeys.length) {
      return false;
    }

    return (
      <SortMenu
        title        = {<>{t('sortBy')}...</>}
        orderKeys    = {orderKeys}
        selectedSort = {selectedSort}
        onSelect     = {newSort => this.handleChangeOrder(newSort)}
      />
    );
  }

  renderLimits() {
    return false;
  }

  renderExports() {
    if (!this.props.exports) {
      return false;
    }

    return (
      <Exports {...this.props.exports}/>
    );
  }

  render() {
    const { isMobile } = this.props
    return (
      <>
        <Paper
          className={classes.paper}
          square
          elevation={0}
          sx={{
            padding: '0 16px',
            borderRadius: '0',

            '@media (min-width: 544px)': {
              padding: '0',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            }
          }}
        >
          <Toolbar
            disableGutters={true}
            classes={{
              root: classes.listingPageToolbar
            }}
          >
            <Typography color="textSecondary" className={classes.listingPageToolbar__totaltext}>{this.props.totalText}</Typography>

            {this.props.suggestOffer && !isMobile ? (
              <div>
                {this.props.suggestOffer}
              </div>
            ) : null}

            {this.renderSort()}

            {this.props.viewComponent}

            {this.props.exportToExcel && (
              <div className={classes.listingPageToolbar__item}>
                <Button onClick={() => this.props.exportToExcel()}>{this.props.t('exportToExcel')}</Button>
              </div>
            )}

            {this.renderExports()}
          </Toolbar>
        </Paper>
        {isMobile && this.props.suggestOffer ? (
          <Box mt={.5}>
            {this.props.suggestOffer}
          </Box>
        ) : null}
      </>
    );
  }
}

export function SortMenu({ selectedSort, orderKeys, title, onSelect }) {
  const selectedSortProp = selectedSort && selectedSort[0] === '-' ? selectedSort.substr(1) : selectedSort;
  const isSelectedSortDesc = selectedSort && selectedSort[0] === '-';

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const onButtonClick = useCallback(evt => {
    setOpen(true);
    setAnchorEl(evt.currentTarget);
  }, [setOpen, setAnchorEl]);

  const onMenuClose = useCallback(evt => {
    setOpen(false);
  }, [setOpen]);

  const onItemClick = useCallback(evt => {
    setOpen(false);
    onSelect(evt.currentTarget.dataset.value);
  }, [setOpen, onSelect]);

  let selectedSortTitle = null;

  const menuItems = orderKeys.map(item => {
    const isSelected = selectedSortProp === item.name;

    if (isSelected) {
      selectedSortTitle = item.title
    }

    if (item.hidden) {
      return null;
    }

    return (
      <MenuItem
        key        = {item.name}
        className  = {classes.sortItem}
        value      = {item.name}
        data-value = {item.name}
        onClick    = {onItemClick}
        selected   = {isSelected}
        dense
      >
        {item.title}
        {
          isSelected ? (
            isSelectedSortDesc ?
              <ArrowDownwardOutlinedIcon className={classes.sortIcon} /> :
              <ArrowUpwardOutlinedIcon className={classes.sortIcon} />
          ) : null
        }
      </MenuItem>
    )
  });

  return (
    <div className={classes.listingPageToolbar__item}>
      <Button
        variant = "text"
        onClick = {onButtonClick}
        endIcon = {selectedSortProp ? (isSelectedSortDesc ? <ArrowDownwardOutlinedIcon /> : <ArrowUpwardOutlinedIcon />) : null}
      >
        {selectedSortTitle || title}
      </Button>
      <Menu
        open            = {open}
        onClose         = {onMenuClose}
        anchorEl        = {anchorEl}
        anchorOrigin    = {{"horizontal":"right","vertical":"top"}}
        transformOrigin = {{"horizontal":"right","vertical":"top"}}
      >
        <MenuItem value="" disabled>{title}</MenuItem>
        {menuItems}
      </Menu>
    </div>
  );
}

SortMenu.propTypes = {
  selectedSort: PropTypes.string,
  orderKeys   : PropTypes.arrayOf(PropTypes.object).isRequired,
  title       : PropTypes.node.isRequired,
  onSelect    : PropTypes.func.isRequired,
}

export class Exports extends Component {

  state = {
    open: false
  }

  static propTypes = {
    title: PropTypes.string,
    items: PropTypes.array,
  }

  static defaultProps = {
    title: '',
    items: [],
  }

  handleTouchTap = (event) => {
    event.preventDefault();
    this.setState({
      open: true,
      anchorEl: event.currentTarget,
    });
  }

  handleRequestClose = () => {
    this.setState({
      open: false,
    });
  }

  renderItems() {
    return this.props.items.map((item, num) => {
      switch (item.type) {
        default:
          return <MenuItem
            dense
            key={num}
            onClick={() => {
              item.onClick();
              if (item.closeAfterClick) {
                this.setState({
                  open: false
                });
              }
            }}
            classes={{
              root: classes.exportMenu
            }}
          >
            <Icon className={classes.exportsItemIcon}>
              {item.icon}
            </Icon>
            {item.title}
          </MenuItem>

        case 'divider':
          return [
            <Divider key={num}/>,
            ...( item.title ? [<Typography variant="subtitle1" className={classes.exportsSubheader} key={num + '-2'}>{item.title}</Typography>] : [])
          ];
      }

    });
  }

  render() {
    return (
      <div>
        <Button variant="text" onClick={this.handleTouchTap}>{this.props.title}</Button>
        <Menu
          open={this.state.open}
          anchorEl={this.state.anchorEl}
          onClose={this.handleRequestClose}
          anchorOrigin={{"horizontal":"right","vertical":"top"}}
          transformOrigin={{"horizontal":"right","vertical":"top"}}
        >
          {this.renderItems()}
        </Menu>
      </div>
    );
  }
}
